import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

import Loader from '../components/Loader'
import Table from '../components/Table'
import Button from '../components/Button'
import FlagFormModal from '../components/experiments/FlagFormModal'
import { ResultSummary } from '../components/experiments/ExperimentResults'
import {
  KindBadge,
  Notice,
  StatusBadge
} from '../components/experiments/ExperimentBadges'
import { Guide } from '../components/experiments/Help'
import { extractErrorMessage } from '../helper'
import { STAGE } from '../helper/stage'
import {
  KIND,
  daysRunning,
  describeAllocation,
  flagList,
  resultFor
} from '../helper/experiments'
import { guardPricingFlagSave } from '../helper/pricing-flag'
import { useFeatureFlags } from '../hooks/use-feature-flags'

const columns = [
  {
    header: 'KEY',
    accessorKey: 'key',
    cell: ({ row }) => (
      <div>
        <Link
          to={`/experiments/${row.original.key}`}
          className='font-mono text-indigo-600 hover:text-indigo-900'
        >
          {row.original.key}
        </Link>
        {row.original.description && (
          <p
            className='max-w-xs truncate text-xs font-normal text-gray-500'
            title={row.original.description}
          >
            {row.original.description}
          </p>
        )}
      </div>
    )
  },
  {
    header: 'ART',
    accessorKey: 'kind',
    cell: (props) => <KindBadge kind={props.getValue()} />
  },
  {
    header: 'STATUS',
    accessorKey: 'status',
    cell: (props) => <StatusBadge status={props.getValue()} />
  },
  {
    header: 'SURFACE',
    accessorKey: 'surface',
    cell: (props) => props.getValue() || '-'
  },
  {
    header: 'UNIT',
    accessorKey: 'unit',
    cell: (props) => props.getValue() || '-'
  },
  {
    header: 'VERTEILUNG',
    id: 'allocation',
    accessorFn: (flag) => describeAllocation(flag),
    cell: ({ row }) => (
      <div>
        <span>{describeAllocation(row.original) || '-'}</span>
        {row.original.winner && (
          <p className='text-xs text-gray-500'>
            Alle bekommen{' '}
            <span className='font-mono'>{row.original.winner}</span>
          </p>
        )}
      </div>
    )
  },
  {
    header: 'LAUFZEIT',
    id: 'days',
    accessorFn: (flag) => daysRunning(flag) ?? -1,
    cell: ({ row }) => {
      const days = daysRunning(row.original)
      return days === null ? '-' : `${days} ${days === 1 ? 'Tag' : 'Tage'}`
    }
  },
  {
    header: 'STAND',
    id: 'result',
    accessorFn: (flag) => flag.result?.decision?.state || '',
    cell: ({ row }) =>
      row.original.kind === KIND.EXPERIMENT ? (
        <ResultSummary result={row.original.result} />
      ) : (
        '-'
      )
  }
]

const Experiments = () => {
  const navigate = useNavigate()
  const {
    flagsQuery,
    resultsQuery,
    saveFlags: rawSaveFlags
  } = useFeatureFlags()
  // pricing_enforcement auf production nur mit doppelter Bestätigung
  const saveFlags = useMemo(
    () => guardPricingFlagSave(rawSaveFlags, { stage: STAGE }),
    [rawSaveFlags]
  )

  const [formOpen, setFormOpen] = useState(false)
  const [formSession, setFormSession] = useState(null)

  const rows = useMemo(
    () =>
      flagList(flagsQuery.data?.flags).map((flag) => ({
        ...flag,
        result: resultFor(resultsQuery.data, flag.key)
      })),
    [flagsQuery.data, resultsQuery.data]
  )

  if (flagsQuery.isLoading) {
    return <Loader loadingText='Experimente werden geladen ...' />
  }

  const base = flagsQuery.data
  const resultsUpdatedAt = resultsQuery.data?.updatedAt

  const openCreate = () => {
    setFormSession({ id: Date.now(), base, flagKey: null })
    setFormOpen(true)
  }

  return (
    <div>
      <div className='sm:flex sm:items-start'>
        <div className='sm:flex-auto'>
          <h1 className='text-base font-semibold leading-6 text-gray-900'>
            Experimente
          </h1>
          <p className='mt-2 max-w-3xl text-sm text-gray-700'>
            Prozent-Rollouts und A/B-Tests aus dem System-Item FEATURE_FLAGS.
            Laufende Experimente mit Unit visitor wertet der Evaluator täglich
            gegen 07:00 UTC aus.
            {resultsUpdatedAt &&
              ` Letzte Auswertung am ${format(new Date(resultsUpdatedAt), 'dd.MM.yyyy HH:mm')}.`}
          </p>
          <Guide />
        </div>
        <div className='mt-4 sm:ml-16 sm:mt-0 sm:flex-none'>
          <Button.PrimaryLG
            text='Neues Flag'
            onClick={openCreate}
            disabled={!base || base.parseError}
          />
        </div>
      </div>

      <div className='mt-4 space-y-2'>
        {flagsQuery.isError && (
          <Notice tone='error'>
            FEATURE_FLAGS konnte nicht geladen werden.{' '}
            {extractErrorMessage(flagsQuery.error)}
          </Notice>
        )}
        {base?.parseError && (
          <Notice tone='error'>
            FEATURE_FLAGS enthält kein gültiges JSON. Speichern ist gesperrt,
            damit nichts überschrieben wird.
          </Notice>
        )}
        {base && !base.exists && (
          <Notice tone='info'>
            Es gibt noch keine Flags. Das System-Item FEATURE_FLAGS entsteht
            beim ersten Speichern.
          </Notice>
        )}
        {resultsQuery.isError && (
          <Notice tone='warning'>
            EXPERIMENT_RESULTS konnte nicht geladen werden, der Stand fehlt
            deshalb. {extractErrorMessage(resultsQuery.error)}
          </Notice>
        )}
      </div>

      <div className='mt-4 flow-root'>
        <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
            <Table data={rows} columns={columns} />
          </div>
        </div>
      </div>

      <FlagFormModal
        open={formOpen}
        setOpen={setFormOpen}
        session={formSession}
        saveFlags={saveFlags}
        onSaved={(key) => navigate(`/experiments/${key}`)}
      />
    </div>
  )
}

export default Experiments
